import { failingFetcher } from '@lib/http/fetch';
import type {
  LookupProductMembershipResponse,
  ProductMembership,
} from '@lib/types/api/ProductListV2';
import { useCallback, useState } from 'react';

const EMPTY_MEMBERSHIP: ProductMembership = {
  personal: [],
  organization: [],
};

type MembershipCache = Record<string, ProductMembership>;

// This hook is only used to track product membership in pages outside account or user list.
// Is required because we need to know which products are in which lists when rendering the page.
export function useProductMembership(
  shouldFetch: boolean,
  organizationId?: string
) {
  const [membership, setMembership] = useState<MembershipCache>({});

  // Validates only if a product is in a Personal List. No organization lists are considered
  const isProductInList = useCallback(
    (productId: string): boolean | null => {
      const productMembership = membership[productId];
      if (productMembership === undefined) return null;
      return productMembership.personal.length > 0;
    },
    [membership]
  );

  const buildLookupUrl = useCallback(
    (encodedIds: string) => {
      const base = `/api/lists/products/lookup?product-ids=${encodedIds}`;
      return organizationId ? `${base}&org-id=${organizationId}` : base;
    },
    [organizationId]
  );

  const fetchProductMembership = useCallback(
    async (productId: string): Promise<ProductMembership> => {
      if (!shouldFetch) return EMPTY_MEMBERSHIP;
      try {
        const url = buildLookupUrl(encodeURIComponent(productId));
        const data = (await failingFetcher(
          url
        )) as LookupProductMembershipResponse;
        const entry = data.products[productId] ?? EMPTY_MEMBERSHIP;
        setMembership((prev) => ({ ...prev, [productId]: entry }));
        return entry;
      } catch (error) {
        console.error('Error fetching product membership:', error);
        return EMPTY_MEMBERSHIP;
      }
    },
    [shouldFetch, buildLookupUrl]
  );

  const fetchBatchProductMembership = useCallback(
    async (productIds: string[]): Promise<void> => {
      if (!shouldFetch || productIds.length === 0) return;
      try {
        const ids = productIds.map(encodeURIComponent).join(',');
        const url = buildLookupUrl(ids);
        const data = (await failingFetcher(
          url
        )) as LookupProductMembershipResponse;

        setMembership((prev) => {
          const next = { ...prev };
          for (const productId of productIds) {
            next[productId] = data.products[productId] ?? EMPTY_MEMBERSHIP;
          }
          return next;
        });
      } catch (error) {
        console.error('Error fetching batch product membership:', error);
      }
    },
    [shouldFetch, buildLookupUrl]
  );

  const addToListOptimistic = useCallback(
    (productId: string, listId: string, isOrgList: boolean) => {
      setMembership((prev) => {
        const current = prev[productId] ?? EMPTY_MEMBERSHIP;
        const key = isOrgList ? 'organization' : 'personal';
        return {
          ...prev,
          [productId]: {
            ...current,
            [key]: [...current[key], listId],
          },
        };
      });
    },
    []
  );

  const removeFromListOptimistic = useCallback(
    (productId: string, listId: string, isOrgList: boolean) => {
      setMembership((prev) => {
        const current = prev[productId] ?? EMPTY_MEMBERSHIP;
        const key = isOrgList ? 'organization' : 'personal';
        return {
          ...prev,
          [productId]: {
            ...current,
            [key]: current[key].filter((id) => id !== listId),
          },
        };
      });
    },
    []
  );

  const rollbackMembership = useCallback(
    (productId: string, previousMembership: ProductMembership) => {
      setMembership((prev) => ({
        ...prev,
        [productId]: previousMembership,
      }));
    },
    []
  );

  const updateMembership = useCallback(
    (productId: string, entry: ProductMembership) => {
      setMembership((prev) => ({
        ...prev,
        [productId]: entry,
      }));
    },
    []
  );

  const getMembership = useCallback(
    (productId: string): ProductMembership =>
      membership[productId] ?? EMPTY_MEMBERSHIP,
    [membership]
  );

  return {
    isProductInList,
    fetchProductMembership,
    fetchBatchProductMembership,
    addToListOptimistic,
    removeFromListOptimistic,
    rollbackMembership,
    setMembership: updateMembership,
    getMembership,
    buildLookupUrl,
  };
}
